import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import useWindowSize from "../hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { format2, format4, numberWithCommas, separateDecimal, separateNumber } from "../utils/numbers";

const DonutChart = ({ data, style = {}, view = "fund", showTitle = true }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const screenSize = useWindowSize();
  const { t } = useTranslation();
  const { theme } = useTheme();

  useEffect(() => {
    if (chartRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current, theme);
    }

    const handleResize = () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
      }
    };
  }, [theme]);

  const { seriesData, categorySeries } = useMemo(() => {
    if (!data) return { seriesData: [], categorySeries: [] };
    const raw = data.map((f) => {
      const price = parseFloat(f?.navData?.nav) || 0;
      const units = parseFloat(f?.fund_unit) || 0;
      const value = f?.fund_unit; // what you requested
      const displayValue = (price * units) || 0;
      return { ...f, value, displayValue };
    });

    const totalValue = raw.reduce((s, r) => s + r.value, 0);
    const safe = Math.max(0, totalValue);

    const series = raw.map((r) => ({
      name: r.symbol_code || r.fund_name || "Untitled",
      value: r.value < 0 ? 0 : parseFloat(r.value),
      rawValue: r.value,
      meta: r,
    }));

    const groups = new Map();

    for (const fund of data || []) {
      const category = (typeof fund?.nature === "string" && fund.nature.trim()) ? fund.nature.trim() : "Unspecified";

      const price = format2(parseFloat(fund?.navData?.nav || 0));
      const units = format4(parseFloat(fund?.fund_unit));

      // const value = (price === 0 || units === 0) ? 0 : price * units;
      const value = units;

      if (!groups.has(category)) {
        groups.set(category, { name: category, amount: 0, funds: [] });
      }

      const entry = groups.get(category);
      entry.value = Number((value)).toFixed(8);
      entry.rawValue = Number((value)).toFixed(8);
      // entry.value = Number((entry.amount + value).toFixed(8));
      // entry.rawValue = Number((entry.amount + value).toFixed(8));
      entry.funds.push({ ...fund, price, units, value });
    }

    const categorySeries = Array.from(groups.values());

    return {
      seriesData: series,
      total: totalValue,
      safeTotal: safe,
      categorySeries
    };
  }, [data]);

  const options = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      title: {
        text: t("portfolio.card.chart"),
        show: showTitle,
        left: "center",
        textStyle: {
          color: theme === "light" ? "rgb(0,0,0)" : "rgb(240,240,240)",
        },
      },
      tooltip: {
        trigger: "item",
        backgroundColor: theme === 'dark' ? 'rgb(44, 49, 66)' : 'rgba(218, 230, 245, 1)',
        textStyle: {
          color: theme === 'dark' ? 'rgb(215, 216, 221)' : '',
          fontSize: 11,
          lineHeight: 11,
        },
        position: (pos, params, el, rect, size) => {
          // pos: [x, y] pointer position; size: { contentSize: [w,h], viewSize: [w,h] }
          const contentW = (size && size.contentSize && size.contentSize[0]) || 140;
          const contentH = (size && size.contentSize && size.contentSize[1]) || 48;
          const viewW = (size && size.viewSize && size.viewSize[0]) || window.innerWidth;
          const viewH = (size && size.viewSize && size.viewSize[1]) || window.innerHeight;

          // For non-small screens, let echarts place it as default
          if (screenSize >= 576) {
            return pos;
          }

          // center coordinates of chart viewport
          const centerX = viewW / 2;
          const centerY = viewH / 2;

          // if pointer is near center, put tooltip at top-center (safe area)
          const DIST_THRESHOLD = 90; // px — how "near" the center counts
          const dx = Math.abs(pos[0] - centerX);
          const dy = Math.abs(pos[1] - centerY);

          if (dx < DIST_THRESHOLD && dy < DIST_THRESHOLD) {
            // top center, leave some margin
            const x = Math.max(8, centerX - contentW / 2);
            const y = 8;
            return [x, y];
          }

          // otherwise prefer to put tooltip below the touch point if there's space,
          // else put it above. Keep it inside view with small margins.
          const margin = 8;
          let x = pos[0] - contentW / 2; // center horizontally relative to finger
          if (x < margin) x = margin;
          if (x + contentW > viewW - margin) x = viewW - contentW - margin;

          // try below
          let y = pos[1] + 12;
          if (y + contentH > viewH - margin) {
            // not enough room below, show above
            y = pos[1] - contentH - 12;
          }
          if (y < margin) y = margin;
          return [x, y];
        },
      },
      color: ["#219ebc", "#ee9b00", "#a7c957", "#c9184a", "#9d4edd"],
      legend: {
        orient: screenSize >= 1200 ? "horizontal" : "horizontal",
        left: screenSize >= 1200 ? "center" : "center",
        top: screenSize >= 1200 ? "bottom" : "bottom",
        padding: 0,
        textStyle: {
          fontSize: screenSize >= 992 ? 11 : 10,
          color: theme === "light" ? "rgb(0,0,0)" : "rgb(250,250,250)",
        },
        itemGap: screenSize >= 992 ? 8 : 6,
        itemWidth: screenSize >= 992 ? 22 : 18,
        itemHeight: screenSize >= 992 ? 13 : 12,
      },
      series: [
        {
          name: "Fund Info",
          type: "pie",
          radius: ["45%", "70%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderWidth: 4,
            borderColor: theme === "light" ? "#dde0ed" : "#4a4c5e",
          },
          label: {
            show: screenSize >= 576 ? true : false,
            position: screenSize >= 576 ? 'outside' : 'center',
            formatter: '{d}%',   
            width: 140,
            fontWeight: 600,
            backgroundColor: "transparent",
            overflow: "truncate",
            ellipsis: "...",
            fontSize: screenSize >= 576 ? 14 : 28
            // position: 'center'
          },
          emphasis: {
            label: {
              show: screenSize >= 576 ? false : true,
              fontSize: screenSize >= 576 ? 12 : 28,
              fontWeight: "bold",
            },
          },
          labelLine: {
              show: screenSize >= 576 ? false : true,
          },
          data: view === 'fund' ? seriesData : categorySeries,
          // data: [
          //   { value: 1048, name: "Fund Actual Fund" },
          //   { value: 735, name: "Cash Fund" },
          //   { value: 580, name: "Financial Planning" },
          //   { value: 484, name: "Aggressive Income Fund" },
          //   { value: 300, name: "Asset Allocation Fund" },
          // ],
        },
      ],
    };
  }, [seriesData, screenSize, theme, view, showTitle]);

  useEffect(() => {
    const inst = chartInstanceRef.current;
    if (!inst) return;

    if (view === 'fund' && options) {
      inst.setOption(options, { notMerge: true })
      inst.resize();
    } else {
      inst.setOption(options, { notMerge: true })
      inst.resize();
    }
    // if (chartInstanceRef.current && options) {
    //   chartInstanceRef.current.setOption(options, { notMerge: true });
    // }
  }, [options, view]);

  return (
    <>
      <div
        ref={chartRef}
        style={{
          // display: view === 'fund' ? 'block' : 'none',
          // width: '100%',
          // height: '300px',
          // minHeight: '350px',
          ...style,
        }}
      />
      {/* {view === 'category' ? (
        <div className="" style={{
          minHeight: '320px'
        }}>
          <div className="text-center" style={{
            fontSize: '22px',
            color: theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.75)",
          }}>
            Fund Allocation (Category)
          </div>
          <div className='p-0'>
            <hr style={{ color: theme === "dark" ? "rgba(255,255,255,.8)" : "rgba(0, 0, 0, 0.6)", marginBlock: ".75rem" }} />
          </div>
          <div className="">
            {categorySeries?.map((item, index) => {
              const amount = separateNumber(item?.amount, { format: true, digits: 2 });

              return (
                <>
                <div className="row w-100 mx-auto justify-content-between align-items-center flex-nowrap" key={index}>
                  <div className="col-auto p-0">
                    <div className="" style={{
                      fontSize: '14px',
                      color: theme === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.5)",
                    }}>
                      Category
                    </div>
                    <div className="" style={{
                      fontSize: '22px',
                      color: theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.75)",
                      lineHeight: '1.1'
                    }}>
                      {item?.name}
                    </div>
                  </div>
                  <div className="col-auto p-0 text-right-fixed">
                    <div className="" style={{
                      fontSize: '14px',
                      color: theme === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.5)",
                    }}>
                      Total Return
                    </div>
                    <div className="" style={{
                      fontSize: '22px',
                      color: theme === "light" ? "rgb(0,0,0)" : "rgb(237, 153, 243)",
                      lineHeight: '1.1'
                    }}>
                      {numberWithCommas(amount?.whole) + '.'}
                      <span style={{
                        fontSize: '18px',
                        color: theme === "light" ? "rgba(0,0,0, .5)" : "rgba(237, 153, 243, .75)",
                      }}>
                        {amount?.decimal}
                      </span>
                    </div>
                  </div>
                </div>

              {index <= categorySeries.length && (
                <div className='col-12 p-0' key={String.fromCharCode(65 + index) + index}>
                  <hr
                    className='mx-auto'
                    style={{
                      maxWidth: "calc(100% - 10px)",
                      marginBlock: '.75rem',
                      color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.45)`,
                    }}
                  />
                </div>
              )}
              </>
              )
            })}
          </div>
        </div>
      ) : ''} */}
    </>
    )
};

export default DonutChart;
